<template>
  <LoginPage v-if="isLogin" />
  <div
    v-else
    class="amt-layout-root crm-shell"
    :class="{
      'is-collapsed': collapsed,
      'mobile-menu-open': mobileMenuOpen,
      'has-assistant': assistantOpen,
      'assistant-expanded': assistantOpen && assistantExpanded,
    }"
    :style="assistantWidth ? { '--assistant-custom-width': `${assistantWidth}px` } : undefined"
    @keydown.esc="
      mobileMenuOpen = false;
      assistantOpen && setAssistantOpen(false);
    "
  >
    <a class="amt-skip-link" href="#amt-main-content">跳到主要内容</a>
    <button
      v-if="mobileMenuOpen"
      class="crm-mobile-menu-backdrop"
      aria-label="关闭导航菜单"
      @click="mobileMenuOpen = false"
    />
    <aside class="amt-aside">
      <div class="amt-brand" :class="{ 'has-company-logo': companyLogo }">
        <img
          v-if="companyLogo"
          class="amt-company-logo"
          :src="companyLogo"
          :alt="`${readSession()?.tenant.name ?? '公司'} Logo`"
          @error="companyLogo = ''"
        />
        <span v-else class="amt-brand-mark" aria-hidden="true"><i /><i /><i /><i /></span>
        <span class="amt-brand-copy">
          <strong class="amt-system-name">AI售前工作台</strong>
          <small>SALES INTELLIGENCE</small>
        </span>
      </div>

      <div ref="menuScroll" class="amt-menu-scroll" @scroll.passive="saveMenuScroll">
        <nav v-for="group in visibleMenuGroups" :key="group.label" class="amt-menu-group">
          <button
            class="amt-menu-group-title amt-menu-group-toggle"
            type="button"
            :aria-expanded="groupOpen(group)"
            @click="toggleGroup(group.label)"
          >
            {{ group.label }}
            <span aria-hidden="true">{{ groupOpen(group) ? '−' : '+' }}</span>
          </button>
          <a
            v-for="item in group.items"
            :key="item.path"
            v-show="groupOpen(group)"
            :href="item.path"
            class="amt-menu-item"
            :class="{ active: isActive(item.path) }"
            :aria-current="isActive(item.path) ? 'page' : undefined"
            :title="item.label"
            @click.prevent="go(item.path)"
          >
            <span class="amt-menu-spine" aria-hidden="true" />
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.label }}</span>
          </a>
        </nav>
      </div>

      <button
        class="amt-collapse"
        type="button"
        :aria-expanded="!collapsed"
        aria-label="切换侧边菜单"
        @click="toggleCollapsed"
      >
        <el-icon><Fold v-if="!collapsed" /><Expand v-else /></el-icon>
        <span>{{ collapsed ? '展开菜单' : '收起菜单' }}</span>
      </button>
    </aside>

    <button
      ref="assistantLauncher"
      type="button"
      class="amt-assistant-launcher"
      :class="{ 'is-active': assistantOpen }"
      :aria-expanded="assistantOpen"
      aria-controls="amt-assistant-panel"
      :aria-label="assistantOpen ? '收起 AI 助手' : '打开 AI 助手'"
      title="AI 助手（⌘J / Ctrl+J）"
      @click="toggleAssistant"
    >
      <img :src="assistantAvatar" alt="" width="40" height="40" />
      <span class="amt-assistant-launcher-label">AI 助手</span>
    </button>

    <section class="amt-workspace">
      <header class="amt-topbar">
        <div class="amt-route-title">
          <button
            class="crm-menu-toggle"
            type="button"
            aria-label="切换侧边菜单"
            :aria-expanded="mobileViewport ? mobileMenuOpen : !collapsed"
            @click="toggleCollapsed"
          >
            <el-icon><Fold v-if="!collapsed" /><Expand v-else /></el-icon>
          </button>
          <span>{{ activeGroup }}</span>
          <span>/</span>
          <strong>{{ routeTitle }}</strong>
        </div>

        <button
          type="button"
          class="amt-command"
          aria-label="搜索业务或告诉 AI 要做什么"
          @click="commandOpen = true"
        >
          <el-icon><Search /></el-icon>
          <span>搜索客户、询价、报价，或告诉 AI 要做什么…</span>
          <kbd>⌘ K</kbd>
        </button>

        <div class="amt-top-actions">
          <button
            type="button"
            class="amt-icon-button"
            :aria-label="`站内消息，${noticeUnread ?? '—'} 条待关注`"
            @click="go('/notifications')"
          >
            <el-icon><Bell /></el-icon>
            <span v-if="noticeUnread" class="crm-notice-badge">{{
              noticeUnread > 99 ? '99+' : noticeUnread
            }}</span>
          </button>
          <el-dropdown trigger="click">
            <button
              type="button"
              class="amt-user-button"
              :aria-label="`当前账号：${userName}，打开账号菜单`"
            >
              <span class="crm-user-avatar" aria-hidden="true">{{ userName.slice(0, 1) }}</span>
              <span class="amt-user-copy">
                <strong>{{ userName }}</strong>
                <small>{{
                  isOwner ? '企业管理员' : isQuotationSupervisor ? '报价主管' : '业务人员'
                }}</small>
              </span>
              <el-icon><ArrowDown /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="go('/profile')">我的资料</el-dropdown-item>
                <el-dropdown-item @click="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main id="amt-main-content" class="amt-main" tabindex="-1">
        <OrdersPage v-if="locationPath === '/orders'" />
        <CustomersPage v-if="locationPath === '/customers'" />
        <!-- 常驻 React 表面：列表页仅隐藏，避免 remount 卡在「正在打开…」 -->
        <ReactSurface v-show="locationPath !== '/orders' && locationPath !== '/customers'" />
      </main>
    </section>

    <!-- 打开过一次就常驻(v-show):关掉再打开,对话还在 -->
    <AssistantDock
      v-if="assistantMounted"
      id="amt-assistant-panel"
      v-show="assistantOpen"
      :path="locationPath"
      :expanded="assistantExpanded"
      :active="assistantOpen"
      :width="assistantWidth"
      @resize="resizeAssistant"
      @resize-end="saveAssistantWidth"
      @close="setAssistantOpen(false)"
      @toggle-expanded="assistantExpanded = !assistantExpanded"
    />

    <el-dialog
      v-model="commandOpen"
      title="快速打开"
      width="min(640px, calc(100vw - 32px))"
      destroy-on-close
      append-to-body
      @opened="focusCommandInput"
    >
      <el-input
        ref="commandInput"
        v-model="keyword"
        placeholder="搜索客户、询价、报价、审批或设置…"
        aria-label="搜索应用页面"
        :prefix-icon="Search"
        @keyup.enter="openFirstResult"
      />
      <p v-if="!filteredMenu.length" class="workspace-search-empty">
        没有匹配的页面，试试“报价”或“客户”。
      </p>
      <div class="amt-command-results">
        <a
          v-for="item in filteredMenu"
          :key="item.path"
          :href="item.path"
          @click.prevent="go(item.path)"
        >
          <el-icon><component :is="item.icon" /></el-icon><span>{{ item.label }}</span>
        </a>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import {
  ArrowDown,
  Bell,
  Box,
  ChatLineSquare,
  Check,
  Collection,
  DataAnalysis,
  Document,
  Expand,
  Fold,
  House,
  OfficeBuilding,
  Opportunity,
  PriceTag,
  Search,
  Setting,
  ShoppingCart,
  Tickets,
  Timer,
  TrendCharts,
  UserFilled,
} from '@element-plus/icons-vue';
import {
  ElDialog,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElIcon,
  ElInput,
} from 'element-plus';
import assistantAvatar from '../assets/assistant-avatar.svg';
import { currentAssistantPreferenceKey } from '../assistant/preferences';
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

import { canDiscoverPage, sceneForPath } from '../lib/page-scenes';
import { clearSession, readSession, sessionActivity, SESSION_CHANGE_EVENT } from '../lib/auth';
import { trpcClient } from '../lib/trpc';
import { LOCATION_CHANGE_EVENT, navigateApp } from '../lib/location-events';
import { matchNavItem, pathMatches } from '../lib/nav-match';
const OrdersPage = defineAsyncComponent(() => import('../views/OrdersPage.vue'));
const CustomersPage = defineAsyncComponent(() => import('../views/CustomersPage.vue'));
const LoginPage = defineAsyncComponent(() => import('../views/LoginPage.vue'));
const ReactSurface = defineAsyncComponent(() => import('../components/ReactSurface.vue'));
const AssistantDock = defineAsyncComponent(() => import('../components/AssistantDock.vue'));

interface MenuItem {
  label: string;
  path: string;
  icon: object;
  ownerOnly?: boolean;
}

const allMenuGroups: Array<{ label: string; items: MenuItem[] }> = [
  {
    label: '工作台',
    items: [
      { label: '今日工作', path: '/', icon: House },
      { label: '站内消息', path: '/notifications', icon: Bell },
      { label: '经营概览', path: '/overview', icon: DataAnalysis },
      { label: '业务自动化', path: '/automation', icon: Opportunity },
    ],
  },
  {
    label: '销售业务',
    items: [
      { label: '客户管理', path: '/customers', icon: UserFilled },
      { label: '询价管理', path: '/inquiries', icon: Document },
      { label: '报价管理', path: '/quotations', icon: Tickets },
      { label: '报价方案', path: '/proposals', icon: ChatLineSquare },
      { label: '报价审批', path: '/approvals', icon: Check },
      { label: '销售跟进', path: '/followups', icon: Timer },
    ],
  },
  {
    label: '订单与复购',
    items: [
      { label: '打样跟踪', path: '/samples', icon: Box },
      { label: '订单跟踪', path: '/orders', icon: ShoppingCart },
      { label: '复购机会', path: '/renewals', icon: TrendCharts },
      { label: '合同到期', path: '/contracts', icon: TrendCharts },
    ],
  },
  {
    label: '知识中心',
    items: [
      { label: '企业知识库', path: '/knowledge', icon: Opportunity },
      { label: '竞品价格', path: '/competitors', icon: PriceTag },
      { label: '产品别名', path: '/terms', icon: Collection },
    ],
  },
  {
    label: '管理设置',
    items: [
      { label: '材料库', path: '/materials', icon: Box },
      { label: '核价样本', path: '/golden', icon: Check, ownerOnly: true },
      { label: '核价参数', path: '/config/parameters', icon: Setting, ownerOnly: true },
      { label: '提醒规则', path: '/config/reminders', icon: Timer, ownerOnly: true },
      { label: 'AI 配置', path: '/config/model', icon: Setting, ownerOnly: true },
      { label: 'AI 用量与审计', path: '/audit/ai', icon: DataAnalysis, ownerOnly: true },
      { label: '员工管理', path: '/members', icon: UserFilled, ownerOnly: true },
      { label: '企业信息', path: '/config/company', icon: OfficeBuilding, ownerOnly: true },
    ],
  },
];
const allItems = allMenuGroups.flatMap((group) => group.items);
const locationPath = ref(window.location.pathname);
const collapsed = ref(window.localStorage.getItem('amt.sidebar.collapsed') === '1');
const mobileMenuOpen = ref(false);
const mobileViewport = ref(window.matchMedia('(max-width: 760px)').matches);
function syncMenuViewport(): void {
  mobileViewport.value = window.matchMedia('(max-width: 760px)').matches;
  if (!mobileViewport.value) mobileMenuOpen.value = false;
}
const commandOpen = ref(false);
// Enter with the business page visible; reopening preserves the mounted conversation.
const assistantOpen = ref(false);
const assistantLauncher = ref<HTMLButtonElement | null>(null);
const assistantMounted = ref(assistantOpen.value);
const assistantExpanded = ref(false);
function readAssistantWidth(): number | null {
  try {
    const key = currentAssistantPreferenceKey('width');
    const value = Number(key ? localStorage.getItem(key) : 0);
    return Number.isFinite(value) && value >= 340 && value <= 760 ? value : null;
  } catch {
    return null;
  }
}
const assistantWidth = ref<number | null>(readAssistantWidth());
function resizeAssistant(width: number): void {
  assistantExpanded.value = false;
  assistantWidth.value = width;
}
function saveAssistantWidth(width: number): void {
  try {
    const key = currentAssistantPreferenceKey('width');
    if (key) localStorage.setItem(key, String(width));
  } catch {
    /* resizing also works without storage */
  }
}

function setAssistantOpen(open: boolean): void {
  assistantOpen.value = open;
  if (open) assistantMounted.value = true;
  if (open) mobileMenuOpen.value = false;
  else {
    assistantExpanded.value = false;
    void nextTick(() => assistantLauncher.value?.focus());
  }
}
const toggleAssistant = () => setAssistantOpen(!assistantOpen.value);
const keyword = ref('');
const commandInput = ref<{ focus: () => void } | null>(null);
const menuScroll = ref<HTMLElement | null>(null);
const session = readSession();
const isOwner = session?.user.role === 'owner';
const isQuotationSupervisor = session?.user.isQuotationSupervisor === true;
function canSeeItem(item: MenuItem): boolean {
  if (item.path === '/approvals') return isOwner || isQuotationSupervisor;
  return (!item.ownerOnly || isOwner) && canDiscoverPage(item.path, isOwner);
}
const visibleMenuGroups = computed(() =>
  allMenuGroups
    .map((group) => ({ ...group, items: group.items.filter(canSeeItem) }))
    .filter((group) => group.items.length)
);
const expandedGroups = ref(new Set(allMenuGroups.map((group) => group.label)));
function groupOpen(group: { label: string; items: MenuItem[] }): boolean {
  return collapsed.value || expandedGroups.value.has(group.label);
}
function toggleGroup(label: string): void {
  const next = new Set(expandedGroups.value);
  if (next.has(label)) next.delete(label);
  else next.add(label);
  expandedGroups.value = next;
}

/**
 * ⚠ 没有登录态就别渲染后台外壳。
 *   以前不管有没有会话都照渲染,菜单、面包屑、水印一应俱全,还挂着一个
 *   凭空来的「管理员」——那是**编造身份**。接口全 401(数据没漏),
 *   但界面把它显示成「一切正常,今天 0 项待办」。
 */
if (!session && window.location.pathname !== '/login') {
  window.location.assign('/login');
}

// ⚠ 不给名字兜底成「管理员」:不知道是谁就别写一个名字上去
const userName = ref(session?.user.name ?? '');
const syncUserName = () => {
  userName.value = readSession()?.user.name ?? '';
};

/** 用户菜单里的跳转。⚠ 路径写在这里,导航覆盖测试会把它也算作「可达」 */
function go(path: string): void {
  mobileMenuOpen.value = false;
  commandOpen.value = false;
  if (path === window.location.pathname) return;
  navigateApp(path);
}

function toggleCollapsed(): void {
  if (window.matchMedia('(max-width: 760px)').matches) {
    mobileMenuOpen.value = !mobileMenuOpen.value;
    return;
  }
  collapsed.value = !collapsed.value;
  window.localStorage.setItem('amt.sidebar.collapsed', collapsed.value ? '1' : '0');
}

function saveMenuScroll(): void {
  if (menuScroll.value) {
    window.sessionStorage.setItem('amt.sidebar.scrollTop', String(menuScroll.value.scrollTop));
  }
}

const isLogin = computed(() => locationPath.value === '/login');
// ⚠ 匹配不上时不要退回第一项:那样未知路由会显示成「今日 / 工作台」,
//   而正文是 Not Found —— 面包屑说的和屏幕上的不是一回事
// ⚠ 按路径段边界 + 最长前缀匹配，列表与详情（/customers/$id）高亮同一侧栏项
const matchedItem = computed(() => matchNavItem(locationPath.value, allItems));
const activeItem = computed(() => matchedItem.value ?? allItems[0]!);
const activeScene = computed(() => sceneForPath(locationPath.value));
// 顶栏标题与 SceneHeader / React Page 共用 page-scenes，避免列表→详情文案跳变
const routeTitle = computed(() =>
  matchedItem.value || locationPath.value === '/profile'
    ? (activeScene.value?.title ?? activeItem.value.label)
    : '页面不存在'
);
const activeGroup = computed(() =>
  locationPath.value === '/profile'
    ? '个人账户'
    : matchedItem.value
      ? (allMenuGroups.find((group) => group.items.includes(matchedItem.value!))?.label ?? '首页')
      : '未知页面'
);
const filteredMenu = computed(() => {
  const query = keyword.value.trim().toLowerCase();
  const accessible = allItems.filter(canSeeItem);
  return query
    ? accessible.filter(
        (item) => item.label.toLowerCase().includes(query) || item.path.includes(query)
      )
    : accessible;
});

const openFirstResult = () => {
  if (filteredMenu.value[0]) go(filteredMenu.value[0].path);
};
const focusCommandInput = () => {
  keyword.value = '';
  commandInput.value?.focus();
};

const isActive = (path: string) => pathMatches(locationPath.value, path);
const syncLocation = () => {
  locationPath.value = window.location.pathname;
};
const onDocumentClick = () => window.setTimeout(syncLocation, 0);
const onKeyDown = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    commandOpen.value = true;
  }
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
    event.preventDefault();
    toggleAssistant();
  }
};
const logout = async () => {
  try {
    await trpcClient.auth.logout.mutate();
  } finally {
    clearSession();
    window.location.assign('/login');
  }
};
let sessionTimer: ReturnType<typeof setInterval>;
const markActivity = (event: Event) => {
  if (event.isTrusted) sessionActivity.mark();
};
const checkSession = () => {
  if (window.location.pathname === '/login') return;
  if (!readSession() || sessionActivity.expired()) {
    void logout();
    return;
  }
  if (sessionActivity.recent())
    void trpcClient.auth.me
      .query()
      .then((result) => {
        if (!result.authenticated) void logout();
      })
      .catch(() => undefined);
};

const companyLogo = ref('');
let brandingRequest = 0;
async function refreshCompanyLogo() {
  if (document.hidden || !readSession()) return;
  const request = ++brandingRequest;
  const tenantId = readSession()?.tenant.id;
  try {
    const company = await trpcClient.tenant.current.query();
    if (request !== brandingRequest || readSession()?.tenant.id !== tenantId) return;
    companyLogo.value = company.quotationProfile.logoBase64
      ? `data:image/png;base64,${company.quotationProfile.logoBase64}`
      : '';
  } catch {
    /* Keep the last successfully loaded brand on temporary failures. */
  }
}
const noticeUnread = ref<number | null>(null);
let noticeTimer: ReturnType<typeof setInterval> | undefined;
async function refreshNotices() {
  if (document.hidden || !readSession()) return;
  try {
    noticeUnread.value = (await trpcClient.notifications.unreadCount.query()).count;
  } catch {
    noticeUnread.value = null;
  }
}
onMounted(() => {
  for (const name of ['pointerdown', 'keydown', 'wheel'])
    window.addEventListener(name, markActivity, { passive: true });
  sessionTimer = setInterval(checkSession, 15000);
  window.addEventListener(SESSION_CHANGE_EVENT, syncUserName);
  window.addEventListener('crm.company-branding.changed', refreshCompanyLogo);
  window.addEventListener('focus', refreshCompanyLogo);
  void refreshCompanyLogo();
  window.addEventListener('resize', syncMenuViewport);
  void refreshNotices();
  noticeTimer = setInterval(() => void refreshNotices(), 30000);
  document.addEventListener('visibilitychange', refreshNotices);
  window.addEventListener('crm.notifications.changed', refreshNotices);
  window.addEventListener('popstate', syncLocation);
  window.addEventListener(LOCATION_CHANGE_EVENT, syncLocation);
  window.addEventListener('keydown', onKeyDown);
  document.addEventListener('click', onDocumentClick);
  void nextTick(() => {
    const saved = Number(window.sessionStorage.getItem('amt.sidebar.scrollTop') ?? 0);
    if (menuScroll.value && Number.isFinite(saved)) menuScroll.value.scrollTop = saved;
  });
});
onBeforeUnmount(() => {
  clearInterval(sessionTimer);
  for (const name of ['pointerdown', 'keydown', 'wheel'])
    window.removeEventListener(name, markActivity);
  window.removeEventListener(SESSION_CHANGE_EVENT, syncUserName);
  window.removeEventListener('crm.company-branding.changed', refreshCompanyLogo);
  window.removeEventListener('focus', refreshCompanyLogo);
  brandingRequest++;
  window.removeEventListener('resize', syncMenuViewport);
  clearInterval(noticeTimer);
  document.removeEventListener('visibilitychange', refreshNotices);
  window.removeEventListener('crm.notifications.changed', refreshNotices);
  window.removeEventListener('popstate', syncLocation);
  window.removeEventListener(LOCATION_CHANGE_EVENT, syncLocation);
  window.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('click', onDocumentClick);
});
</script>

<style scoped>
.amt-company-logo {
  width: 160px;
  height: 50px;
  max-width: 100%;
  flex: none;
  object-fit: contain;
  object-position: left center;
  background: white;
  border-radius: 8px;
}
.amt-brand.has-company-logo {
  min-height: 94px;
  height: auto;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;
  padding: 12px 20px;
}
.amt-brand.has-company-logo .amt-brand-copy {
  gap: 0;
}
.amt-brand.has-company-logo .amt-system-name {
  font-size: 13px;
}
.amt-brand.has-company-logo .amt-brand-copy small {
  display: none;
}
.crm-shell.is-collapsed .amt-brand.has-company-logo {
  align-items: center;
  padding: 10px 8px;
}
.crm-shell.is-collapsed .amt-company-logo {
  width: 48px;
  height: 48px;
  object-position: center;
}
.crm-shell.mobile-menu-open .amt-brand.has-company-logo {
  align-items: flex-start;
  padding: 12px 20px;
}
.crm-shell.mobile-menu-open .amt-company-logo {
  width: 160px;
  height: 50px;
  object-position: left center;
}
.crm-notice-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 1px 4px;
  border-radius: 8px;
  background: var(--el-color-primary);
  color: white;
  font-size: 10px;
}
.amt-icon-button {
  position: relative;
}
.amt-menu-group-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.amt-menu-group-toggle:focus-visible {
  outline: 2px solid #087f6d;
  outline-offset: -2px;
}
/* 窄屏侧栏只显示图标时，不能把分组内入口隐藏。 */
@media (min-width: 821px) and (max-width: 1100px) {
  .amt-menu-group-toggle {
    display: none;
  }
  .amt-menu-item {
    display: flex !important;
  }
}
</style>
